import type { IAccountRepository, DetectAccountByCountryResult } from '@/domain/repositories/account.repository'
import type { Account } from '@/domain/entities/account.entity'
import type { AccountResponseDto } from '@bibleawr/landing2-contract'
import { accountsContract } from '@bibleawr/landing2-contract'
import { createLandingTsRestClient } from '../api/ts-rest-client'
import { getApiBaseUrl } from '../api/endpoints'
import { ApiError } from '../errors'

function mapResponseToAccount (dto: AccountResponseDto & {
  account_country_codes?: string[]
  account_country_codes_defaults?: string
}): Account {
  return {
    _id: dto._id,
    account_name: dto.account_name,
    account_logo: dto.account_logo,
    account_site: dto.account_site,
    account_access: dto.account_access,
    account_courses: dto.account_courses,
    account_locale: dto.account_locale,
    account_has_teams: dto.account_has_teams,
    account_locales_ui: dto.account_locales_ui,
    account_country_iso: dto.account_country_iso,
    account_country_codes: dto.account_country_codes,
    account_country_codes_defaults: dto.account_country_codes_defaults,
    account_token: dto.account_token,
    account_extra_props: dto.account_extra_props,
    account_config_landing: dto.account_config_landing,
  }
}

export class AccountRepository implements IAccountRepository {
  private readonly client = createLandingTsRestClient(accountsContract)

  // TODO: migrate to contract — GET /accounts/:id not in accountsContract yet
  async findById (accountId: string): Promise<Account | null> {
    const baseUrl = getApiBaseUrl('LANDING').replace(/\/$/, '')
    const response = await fetch(`${baseUrl}/accounts/${encodeURIComponent(accountId)}`)
    if (response.status === 404) return null
    if (!response.ok) throw new ApiError(`Failed to fetch account by id`, response.status)
    const body = await response.json().catch(() => null)
    const dto = body?.data ?? body
    if (!dto?._id) return null
    return mapResponseToAccount(dto)
  }

  async detectByCountry (countryIso: string): Promise<DetectAccountByCountryResult> {
    const res = await this.client.detectByCountry({ params: { country_iso: countryIso } })
    if (res.status !== 200) throw new ApiError(`detectByCountry failed`, res.status)
    const { data, fallback_used } = res.body
    const fallbackUsed = fallback_used ?? false
    if (!Array.isArray(data) || data.length === 0) return { account: null, fallbackUsed }
    return { account: mapResponseToAccount(data[0]), fallbackUsed }
  }
}
